import { DatePipe } from '@angular/common';
import { httpResource } from '@angular/common/http';
import { Component, computed, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { Api, byModelName, errorMessage, ModelInfo } from '../api';
import { UserService } from '../user.service';

const MAX_MODELS = 6;
/** "qwen3" from "qwen3:30b". */
const family = (name: string) => name.split(':')[0];
/** "30b" from "qwen3:30b". */
const size = (name: string) => name.split(':')[1] ?? 'latest';

/** Sends one prompt to several models at once. */
@Component({
  selector: 'app-multi',
  imports: [RouterLink, DatePipe],
  templateUrl: './multi.html',
  styles: `
    .models {
      display: grid;
      gap: 0.5rem;
      margin: 1rem 0;
    }
    .pick {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      align-items: center;
    }
    .pick > span {
      min-width: 5rem;
    }
    textarea {
      width: 100%;
      min-height: 6rem;
      field-sizing: content;
    }
  `,
})
export class Multi {
  private api = inject(Api);
  private router = inject(Router);
  users = inject(UserService);

  models = httpResource<ModelInfo[]>(() => '/api/models', { defaultValue: [] });
  /** Models grouped by family (qwen3, gemma4, …), each with its sizes smallest first. */
  families = computed(() => {
    const byFamily = new Map<string, ModelInfo[]>();
    for (const m of this.models.value()) {
      const f = family(m.name);
      byFamily.set(f, [...(byFamily.get(f) ?? []), m]);
    }
    return [...byFamily]
      .map(([name, models]) => ({
        name,
        models: models.sort((a, b) => byModelName(size(a.name), size(b.name))),
      }))
      .sort((a, b) => byModelName(a.name, b.name));
  });
  history = httpResource<{ id: string; prompt: string; created: string }[]>(
    () => (this.users.identityKey() ? '/api/multis' : undefined),
    { defaultValue: [] },
  );
  prompt = signal('');
  selected = signal<string[]>([]);
  sending = signal(false);
  error = signal<string | null>(null);
  canSend = computed(
    () =>
      this.prompt().trim().length > 0 &&
      this.selected().length >= 2 &&
      this.selected().length <= MAX_MODELS,
  );
  /** One row per chosen model, plus an empty one for the next until there are MAX_MODELS. */
  rows = computed(() => {
    const picks: (string | null)[] = this.selected();
    return picks.length < MAX_MODELS ? [...picks, null] : picks;
  });

  readonly family = family;
  readonly size = size;

  /** Models picked in rows other than row i, which that row can't pick again. */
  takenElsewhere(i: number): Set<string> {
    return new Set(this.selected().filter((_, j) => j !== i));
  }

  sizesOf(familyName: string): ModelInfo[] {
    return this.families().find((f) => f.name === familyName)?.models ?? [];
  }

  /** Choosing a family picks its most-requested size; choosing none removes the row. */
  setFamily(i: number, familyName: string) {
    if (!familyName) {
      this.selected.update((s) => s.filter((_, j) => j !== i));
      return;
    }
    const taken = this.takenElsewhere(i);
    const choice = this.sizesOf(familyName)
      .map((m, order) => ({ m, order }))
      .filter(({ m }) => !taken.has(m.name))
      .sort(
        (a, b) =>
          (b.m.requests ?? 0) - (a.m.requests ?? 0) || b.m.nodes - a.m.nodes || a.order - b.order,
      )[0]?.m;
    if (choice) this.setModel(i, choice.name);
  }

  setModel(i: number, name: string) {
    this.selected.update((s) => {
      const next = [...s];
      next[i] = name;
      return next;
    });
  }

  async send() {
    if (!this.canSend()) return;
    this.sending.set(true);
    this.error.set(null);
    try {
      const { id } = await this.api.post<{ id: string }>('/api/multis', {
        query: this.prompt(),
        models: this.selected(),
      });
      this.router.navigate(['/m', id]);
    } catch (err) {
      this.error.set(errorMessage(err));
    } finally {
      this.sending.set(false);
    }
  }
}
